import dayjs from "dayjs";
import { DatePicker } from "antd";
import { Link } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import Select, { MultiValue, SingleValue } from "react-select";

import { showErrorMessage, showSuccessMessage } from "../common/message";
import { CAMPAIGN_STATUSES, CAMPAIGN_TYPES, TARGET_AUDIENCES } from "../../feature-module/crm/campaign/config";
import { addCampaign, deleteCampaign, getCampaignDetail, updateCampaign } from "../../feature-module/crm/campaign/campaignAPI";

type CampaignFormData = {
  name?: string,
  value?: number,
  description?: string,
}

const defaultCampaignFormData = {
  name: "",
  value: 0,
  description: "",
}

type CampaignModalProps = {
  action?: "edit" | "delete"
  recordId?: number,
  setRecordId: (recordId: any) => void;
  reload: boolean,
  setReload: (reload: any) => void; // Function that takes no arguments and returns void
};

type CampaignPayloadType = {
  type: string | undefined;
  targetAudience: string | undefined;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
  status?: string;
  name?: string | undefined;
  value?: number | undefined;
  description?: string | undefined;
  [key: string]: any; // Add this index signature
};

type campaignFieldType = {
  name: string,
  value: string,
  description: string,
  type: string,
  targetAudience: string,
  startDate: string,
  endDate: string,
  status: string,
  [key: string]: any;
}

const addKeys = [
  "name", 
  "type",
  "value", 
  "startDate", 
  "endDate", 
  "targetAudience"
];

const editKeys = [
  "name", 
  "type",
  "value", 
  "startDate", 
  "endDate", 
  "targetAudience", 
  "status",
];

const campaignField: campaignFieldType = {
  name: "Name",
  value: "Deal Value",
  description: "Description",
  type: "Campaign Type",
  targetAudience: "Target Audience",
  startDate: "Start Date",
  endDate: "End Date",
  status: "Staus",
}


const CampaignModal = ({action, recordId, setRecordId, reload, setReload}: CampaignModalProps) => {
  const [formData, setFormData] = useState<CampaignFormData>(defaultCampaignFormData);
  const [type, setType] = useState<SingleValue<{value: string; label: string;}>>();
  const [target, setTarget] = useState<MultiValue<{value: string; label: string;}>>();
  const [status, setStatus] = useState<SingleValue<{value: string; label: string;}>>();
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [isCampaignDtlLoading, setIsCampaignDtlLoading] = useState(false);

  const cancelAddRef = useRef<HTMLButtonElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchCampaignDetail = async () => {
      try {
        setIsCampaignDtlLoading(true);
        const { data } = await getCampaignDetail(recordId);
        const dtlData = data?.data;

        setFormData({
          name: dtlData?.name,
          value: dtlData?.value,
          description: dtlData?.description || "",
        });

        let typeOption = CAMPAIGN_TYPES.filter(cpType => cpType.value === dtlData?.type)[0];
        setType(typeOption);

        let statusOption = CAMPAIGN_STATUSES.filter(cpStatus => cpStatus.value === dtlData?.status)[0];
        setStatus(statusOption);

        const targetAu = dtlData?.targetAudience?.split(",");
        const targetOptions = TARGET_AUDIENCES.filter(t => targetAu && targetAu.includes(t.value));
        setTarget(targetOptions);

        setStartDate(dtlData?.startDate ? dayjs(dtlData.startDate) : null);
        setEndDate(dtlData?.endDate ? dayjs(dtlData.endDate) : null);

        console.log(dtlData);
      } catch (error) {
        console.error("Error fetching campaign details:", error);
      }
      setIsCampaignDtlLoading(false);
    };

    if (action === "edit" && recordId) {
      fetchCampaignDetail();
    }
  }, [recordId, action]);

  const initFormData = () => {
    setFormData(defaultCampaignFormData);
    setType(null);
    setTarget([]);
    setStatus(null);
    setStartDate(null);
    setEndDate(null);
    setRecordId(null);
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const newFormData = { ...formData, [e.target.name]: e.target.value };
    setFormData(newFormData);
  }

  const handleStartDateChange = (value: dayjs.Dayjs) => {
    setStartDate(value);
  }

  const handleEndDateChange = (value: dayjs.Dayjs) => {
    setEndDate(value);
  }

  const handleTypeChange = (option: SingleValue<{value: string; label: string;}>) => {
    setType(option);
  }

  const handleTargetAudienceChange = (selectedOptions: MultiValue<{value: string; label: string;}>) => {
    setTarget(selectedOptions);
  };
  

  const isValidPayload = (payload: CampaignPayloadType, keys: string[]) => {
    let error = "";
    keys.forEach(key => {
      if ([undefined, "", null].includes(payload[key])) {
        if (!error) {
          error = `${campaignField[key]} is required`;
        }
      }
    });
    
    if (error) {
      showErrorMessage(error);
      return false
    }
    return true;
  }

  const handleCreateCampaign = async () => {
    try {
      const payload: CampaignPayloadType = {
        ...formData,
        type: type?.value,
        targetAudience: target?.map(t => t.value)?.join(","),
        startDate,
        endDate,
        status: CAMPAIGN_STATUSES[0].value
      }

      if (!isValidPayload(payload, addKeys)) {
        return;
      }
  
      await addCampaign(payload);
      
      showSuccessMessage("Added campaign successfully!");
      cancelAddRef.current?.click();
      setReload(!reload);
    } catch(error) {
      console.error("Error adding campaigns:", error);
    }
  }

  const handleEditCampaign = async () => {
    try {
      const payload: CampaignPayloadType = {
        ...formData,
        status: status?.value,
        type: type?.value,
        targetAudience: target?.map(t => t.value)?.join(","),
        startDate,
        endDate
      }

      if (!isValidPayload(payload, editKeys)) {
        return;
      }
  
      if (recordId) {
        await updateCampaign(recordId, payload);
        
        showSuccessMessage("Edited campaign successfully!");
        cancelEditRef.current?.click();
        setReload(!reload);
      }
    } catch(error) {
      console.error("Error updating campaigns:", error);
    }

  }

  const handleDeleteCampaign = async () => {
    if (recordId) {
      try {
        await deleteCampaign(recordId);
        showSuccessMessage("Deleted campaign successfully!");
        setReload(!reload);
      } catch(error) {
        console.error("Error deleting campaigns:", error);
      }
    }
  }

  const addNewCampaignTsx = (
    <div
      className="offcanvas offcanvas-end offcanvas-large"
      tabIndex={-1}
      id="offcanvas_add"
    >
      <div className="offcanvas-header border-bottom">
        <h4>Add New Campaign</h4>
        <button
          type="button"
          className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={initFormData}
        >
          <i className="ti ti-x" />
        </button>
      </div>
      <div className="offcanvas-body">
        <form >
          <div>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input type="text" className="form-control" name="name" onChange={handleInputChange} value={formData.name}/>
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Campaign Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={type}
                    options={CAMPAIGN_TYPES}
                    className="select"
                    placeholder="Choose"
                    classNamePrefix="react-select"
                    onChange={handleTypeChange}
                  />
                </div>
              </div>
              <div className="col-lg-6 col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker className="form-control" required onChange={handleStartDateChange} value={startDate} />
                 
                </div>
              </div>
              <div className="col-lg-6 col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker className="form-control" required onChange={handleEndDateChange} value={endDate} />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Target Audience <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    name="colors"
                    options={TARGET_AUDIENCES}
                    className="select"
                    onChange={handleTargetAudienceChange}
                    value={target}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              data-bs-dismiss="offcanvas"
              className="btn btn-light me-2"
              ref={cancelAddRef}
              onClick={initFormData}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleCreateCampaign}>
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const editCampaignTsx = (
    <div
      className="offcanvas offcanvas-end offcanvas-large"
      tabIndex={-1}
      id="offcanvas_edit"
    >
      <div className="offcanvas-header border-bottom">
        <h4>Edit Campaign</h4>
        <button
          type="button"
          className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={initFormData}
        >
          <i className="ti ti-x" />
        </button>
      </div>
      <div className="offcanvas-body">
        <form >
          <div className="pro-create">
          <div>
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input type="text" className="form-control" name="name" onChange={handleInputChange} value={formData.name}/>
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Campaign Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={type}
                    options={CAMPAIGN_TYPES}
                    className="select"
                    placeholder="Choose"
                    classNamePrefix="react-select"
                    onChange={handleTypeChange}
                  />
                </div>
              </div>
              <div className="col-lg-4 col-md-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Deal Value<span className="text-danger"> *</span>
                  </label>
                  <input className="form-control" type="number" name="value" onChange={handleInputChange} value={formData.value}/>
                  
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker className="form-control" required onChange={handleStartDateChange} value={startDate} />
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker className="form-control" required onChange={handleEndDateChange} value={endDate} />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={status}
                    name="status"
                    options={CAMPAIGN_STATUSES.filter((cpStatus, index) => index > CAMPAIGN_STATUSES.indexOf(CAMPAIGN_STATUSES.filter(s => s.value === status?.value)[0]))}
                    className="select"
                    onChange={setStatus}
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="col-form-label">
                    Target Audience <span className="text-danger">*</span>
                  </label>
                  <Select
                    value={target}
                    isMulti
                    name="targetAudience"
                    options={TARGET_AUDIENCES}
                    className="select"
                    onChange={handleTargetAudienceChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    name="description"
                    defaultValue={formData.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>
          </div>
          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              data-bs-dismiss="offcanvas"
              className="btn btn-light me-2"
              ref={cancelEditRef}
              onClick={initFormData}
            >
              Cancel
            </button>
            <button onClick={handleEditCampaign} type="button" className="btn btn-primary" disabled={isCampaignDtlLoading}>
              Edit
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const deleteCampaignTsx = (
    <div className="modal fade" id="delete_campaign" role="dialog">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body">
            <div className="text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                <i className="ti ti-trash-x fs-36 text-danger" />
              </div>
              <h4 className="mb-2">Remove campaign?</h4>
              <p className="mb-0">
                Are you sure you want to remove <br /> campaign you
                selected.
              </p>
              <div className="d-flex align-items-center justify-content-center mt-4">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link onClick={handleDeleteCampaign} to="#" className="btn btn-danger" data-bs-dismiss="modal">
                  Yes, Delete it
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <>
        {addNewCampaignTsx}
        {editCampaignTsx}
      </>
      <>
        {deleteCampaignTsx}
        {/* Add New View */}
        <div className="modal custom-modal fade" id="save_view" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New View</h5>
                <button
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x" />
                </button>
              </div>
              <div className="modal-body">
                <form >
                  <div className="mb-3">
                    <label className="col-form-label">View Name</label>
                    <input type="text" className="form-control" />
                  </div>
                  <div className="modal-btn text-end">
                    <Link
                      to="#"
                      className="btn btn-light"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <button type="submit" className="btn btn-danger">
                      Save
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        {/* /Add New View */}
      </>
    </>
  );
};

export default CampaignModal;
